import { useEffect, useState } from "react";
import {
  Download,
  Smartphone,
  Link2,
  Copy,
  Check,
  LogOut,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { type Bootstrap, type Profile } from "../../shared/domain";
import { PasskeySettings } from "./PasskeySettings";
import { authPost } from "./Login";
import { clearRecoveries } from "../recovery";
import { api, errorMessage } from "../api";
import { Field, Notice, Spinner, Modal } from "./Common";
interface Token {
  hash: string;
  client_id: string;
  scope: string;
  expires_at: number;
}
export function Settings({
  data,
  onRefresh,
}: {
  data: Bootstrap;
  onRefresh: () => Promise<void>;
}) {
  const [business, setBusiness] = useState<Profile>(data.business),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [success, setSuccess] = useState(""),
    [editing, setEditing] = useState(false),
    [tokens, setTokens] = useState<Token[]>([]),
    [newToken, setNewToken] = useState<{
      token: string;
      expires: number;
    } | null>(null),
    [tokenName, setTokenName] = useState("Personal assistant"),
    [copied, setCopied] = useState(false);
  const loadTokens = () =>
    api<Token[]>("/agent-tokens")
      .then(setTokens)
      .catch((e) => setError(errorMessage(e)));
  useEffect(() => {
    void loadTokens();
  }, []);
  const endpoint = `${location.origin}/mcp`;
  return (
    <div className="page-narrow">
      <div className="page-title">
        <div>
          <h1>Settings</h1>
        </div>
      </div>
      {error && <Notice type="error">{error}</Notice>}
      {success && <Notice type="success">{success}</Notice>}
      <section className="settings-section">
        <h2>
          <ShieldCheck size={20} />
          Account
        </h2>
        <p>
          <strong>{data.user.email}</strong>
        </p>
        <p className="muted">
          {data.user.local
            ? "Local development workspace. Shared phone access will be available after private hosting is connected."
            : "Private workspace. Only authorised passkeys can sign in."}
        </p>
        {!data.user.local && (
          <button
            className="secondary inline"
            onClick={async () => {
              try {
                await authPost("logout");
                clearRecoveries(data.user.email);
                location.assign("/");
              } catch (e) {
                setError(errorMessage(e));
              }
            }}
          >
            <LogOut size={17} />
            Sign out
          </button>
        )}
      </section>
      {!data.user.local && <PasskeySettings />}
      <section className="settings-section">
        <h2>
          <Smartphone size={20} />
          Install on iPhone
        </h2>
        <ol className="instructions">
          <li>Open this workspace in Safari.</li>
          <li>
            Tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>
            .
          </li>
          <li>
            Tap <strong>Add</strong>. Open AI Link from your Home Screen.
          </li>
        </ol>
        <p className="muted small">
          An internet connection is needed to open shared records and issue
          invoices. Unsaved draft changes are kept on the device you’re using.
        </p>
      </section>
      <section className="settings-section">
        <h2>Data-only storage</h2>
        <p>
          Only invoice details are saved in your workspace. PDF files are
          created on your device when you export or share them.
        </p>
        <p className="muted small">
          Issued details stay fixed, including customer and bank details. Your
          JSON export is a complete backup of invoice data.
        </p>
      </section>
      <section className="settings-section">
        <div className="section-title">
          <h2>Business & bank details</h2>
          <button
            className="text-button"
            onClick={() => {
              setBusiness(data.business);
              setEditing(true);
            }}
          >
            Edit details
          </button>
        </div>
        <p>
          <strong>{data.business.legalName}</strong>
          <br />
          {data.business.invoiceAddress.line1},{" "}
          {data.business.invoiceAddress.city}
          <br />
          {data.business.invoiceAddress.postcode}
        </p>
        <div className="detail-pairs">
          <span>Company number</span>
          <strong>{data.business.companyNumber}</strong>
          <span>VAT number</span>
          <strong>
            {data.business.vatRegistrationNumber || "Not registered"}
          </strong>
          <span>Account ending</span>
          <strong>
            •••• {data.business.paymentDetails.accountNumber.slice(-4)}
          </strong>
        </div>
        <p className="muted small">
          Issued PDFs keep the details they were created with.
        </p>
      </section>
      <section className="settings-section">
        <h2>
          <Link2 size={20} />
          Connect an assistant
        </h2>
        <p>
          Let a compatible assistant find invoices and prepare drafts. Open the
          app to review and issue each document.
        </p>
        <Field label="MCP server address">
          <div className="copy-field">
            <input readOnly value={endpoint} />
            <button
              className="icon-button"
              aria-label="Copy MCP address"
              onClick={async () => {
                await navigator.clipboard.writeText(endpoint);
                setCopied(true);
                setTimeout(() => setCopied(false), 2500);
              }}
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
            </button>
          </div>
        </Field>
        <p className="muted small">
          Use OAuth in a compatible remote MCP client. For clients supporting
          bearer tokens, create a connection below. Tokens expire after 7 days.
        </p>
        <div className="two-columns token-create">
          <Field label="Connection name">
            <input
              value={tokenName}
              maxLength={80}
              onChange={(e) => setTokenName(e.target.value)}
            />
          </Field>
          <button
            className="secondary"
            disabled={busy || !tokenName.trim()}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                setNewToken(
                  await api("/agent-tokens", "POST", { name: tokenName }),
                );
                await loadTokens();
              } catch (e) {
                setError(errorMessage(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : <PlusIcon />}Create connection
          </button>
        </div>
        {tokens.length > 0 && (
          <ul className="connection-list">
            {tokens.map((t) => (
              <li key={t.hash}>
                <div>
                  <strong>
                    {t.client_id.length > 40
                      ? "Connected assistant"
                      : t.client_id}
                  </strong>
                  <small>
                    Expires{" "}
                    {new Date(t.expires_at * 1000).toLocaleDateString("en-GB")}
                  </small>
                </div>
                <button
                  className="icon-button danger-text"
                  aria-label={`Revoke ${t.client_id}`}
                  onClick={async () => {
                    try {
                      await api(`/agent-tokens/${t.hash}`, "DELETE", {});
                      await loadTokens();
                    } catch (e) {
                      setError(errorMessage(e));
                    }
                  }}
                >
                  <Trash2 size={18} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="settings-section">
        <h2>
          <Download size={20} />
          Keep a copy of your records
        </h2>
        <p>
          Export customers, invoice records, business details and the activity
          log as JSON. Download individual PDFs from each invoice.
        </p>
        <a href="/api/export" className="secondary inline" download>
          <Download size={18} />
          Export records
        </a>
      </section>
      <p className="muted small">AI Link Invoices · Mobile v0.1.0</p>
      {newToken && (
        <Modal
          title="Your assistant connection"
          onClose={() => setNewToken(null)}
        >
          <p>
            Copy this token into your assistant’s bearer-token field. It is only
            shown once.
          </p>
          <label className="field">
            <span>Access token</span>
            <textarea readOnly value={newToken.token} rows={3} />
          </label>
          <p className="muted small">
            Expires {new Date(newToken.expires * 1000).toLocaleString("en-GB")}.
            Keep it private.
          </p>
          <button
            className="primary"
            onClick={async () => {
              await navigator.clipboard.writeText(newToken.token);
              setSuccess("Connection token copied");
              setNewToken(null);
            }}
          >
            <Copy size={18} />
            Copy token
          </button>
        </Modal>
      )}
      {editing && (
        <Modal
          title="Business & bank details"
          onClose={() => setEditing(false)}
        >
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setError("");
              try {
                await api("/business", "PUT", {
                  profile: business,
                  version: data.businessVersion,
                });
                await onRefresh();
                setEditing(false);
                setSuccess("Business details updated");
              } catch (err) {
                setError(errorMessage(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            {error && <Notice type="error">{error}</Notice>}
            {(
              [
                "legalName",
                "tradingName",
                "companyNumber",
                "vatRegistrationNumber",
                "email",
                "telephone",
                "website",
              ] as const
            ).map((k) => (
              <Field
                key={k}
                label={
                  {
                    legalName: "Legal company name",
                    tradingName: "Trading name",
                    companyNumber: "Company number",
                    vatRegistrationNumber: "VAT number",
                    email: "Email",
                    telephone: "Telephone",
                    website: "Website",
                  }[k]
                }
              >
                <input
                  value={business[k]}
                  type={k === "email" ? "email" : "text"}
                  maxLength={150}
                  required={[
                    "legalName",
                    "companyNumber",
                    "email",
                    "telephone",
                  ].includes(k)}
                  onChange={(e) =>
                    setBusiness({ ...business, [k]: e.target.value })
                  }
                />
              </Field>
            ))}
            {(["line1", "line2", "city", "postcode", "country"] as const).map(
              (k) => (
                <Field
                  key={k}
                  label={
                    {
                      line1: "Address line 1",
                      line2: "Address line 2",
                      city: "Town / city",
                      postcode: "Postcode",
                      country: "Country",
                    }[k]
                  }
                >
                  <input
                    value={business.invoiceAddress[k]}
                    required={k !== "line2"}
                    onChange={(e) =>
                      setBusiness({
                        ...business,
                        invoiceAddress: {
                          ...business.invoiceAddress,
                          [k]: e.target.value,
                        },
                      })
                    }
                  />
                </Field>
              ),
            )}
            <h3>Payment details</h3>
            {(["accountName", "sortCode", "accountNumber"] as const).map(
              (k) => (
                <Field
                  key={k}
                  label={
                    {
                      accountName: "Account name",
                      sortCode: "Sort code",
                      accountNumber: "Account number",
                    }[k]
                  }
                >
                  <input
                    value={business.paymentDetails[k]}
                    required
                    onChange={(e) =>
                      setBusiness({
                        ...business,
                        paymentDetails: {
                          ...business.paymentDetails,
                          [k]: e.target.value,
                        },
                      })
                    }
                  />
                </Field>
              ),
            )}
            <div className="dialog-actions">
              <button
                type="button"
                className="secondary"
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
              <button className="primary" disabled={busy}>
                {busy && <Spinner />}Save details
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
function PlusIcon() {
  return <Link2 size={18} />;
}
